import { useLayoutEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsChevronDown, BsChevronUp, BsCircle } from 'react-icons/bs';
import Skeleton from 'react-loading-skeleton';
import ImageGallery from 'react-image-gallery';
// @ts-ignore
import 'react-image-gallery/styles/css/image-gallery.css';
import { useTrims, useVehicle } from '../api';
import type {
	CatalogOption,
	CustomizerTab,
	OptionId,
	SpecRow,
	VehicleSpecs,
} from '../domain';
import { buildStockSpecs } from '../normalize';
import { useConfiguratorState } from '../store';
import { useConfiguratorFeatures } from '../features';
import { formatCurrency, titleCase } from '../common';
import { useConfiguratorTranslations } from '../translations';
import styles from './VehicleSpecPanel.module.scss';

export type LineItem = {
	id: OptionId;
	name: string;
	price: number;
	isPack: boolean;
	members: string[];
};

export type Pricing = {
	listPriceVat: number;
	optionalsTotalVat: number;
	totalVat: number;
	lineItems: LineItem[];
};

const EMPTY_PRICING: Pricing = {
	listPriceVat: 0,
	optionalsTotalVat: 0,
	totalVat: 0,
	lineItems: [],
};

export function usePricing(): Pricing {
	const vehicleRef = useConfiguratorState((s) => s.vehicle);
	const vehicleMode = useConfiguratorState((s) => s.vehicleMode);
	const stockVehicle = useConfiguratorState((s) => s.stockVehicle);
	const committedPicks = useConfiguratorState((s) => s.committedPicks);
	const engine = useConfiguratorState((s) => s.engine);
	const showTwins = useConfiguratorFeatures((f) => f.showTwinInSpec);
	const { data } = useVehicle(vehicleRef);

	/**
	 * In pronta consegna i prezzi arrivano tali e quali dallo stock.
	 */
	const stockPricing = useMemo<Pricing | null>(() => {
		if (!stockVehicle) return null;

		return {
			listPriceVat: stockVehicle.price.listPriceNoVat,
			optionalsTotalVat: stockVehicle.price.optionalsNoVat,
			totalVat: stockVehicle.price.totalNoVat,
			lineItems: stockVehicle.optionals.map((o, i) => ({
				// solo da chiave React, nessuno lo risolve contro il catalogo
				id: i,
				name: o.description || o.code,
				price: o.priceNoVat,
				isPack: false,
				members: [],
			})),
		};
	}, [stockVehicle]);

	const enginePricing = useMemo(() => {
		if (!data || !engine) return EMPTY_PRICING;

		const { catalog, vehicle } = data;
		const { lines, total } = engine.priceBreakdown();
		const pickOrder = new Map(committedPicks.map((id, i) => [id, i]));

		// Vernici collassate: l'engine trascina il gemello (prezzo 0) come riga a sé
		// via `formulaInclusione`, ma il gemello non è in `byId`. Ricostruiamo qui
		// nome e codice del gemello dal master, così la sua riga non resta "268".
		// Con `showTwins = false` la riga del gemello viene nascosta del tutto.
		const twinInfo = new Map<OptionId, { name: string; code: string }>();
		for (const o of catalog.options) {
			if (o.twinId != null) {
				twinInfo.set(o.twinId, {
					name: o.twinName ?? o.name,
					code: o.twinCode ?? '',
				});
			}
		}

		const label = (name: string, code: string): string =>
			code ? `${name} [Cod. ${code}]` : name;

		const nameOf = (id: OptionId, fallback: string): string => {
			const option = catalog.byId.get(id);
			if (option) return label(option.name, option.code);
			const twin = twinInfo.get(id);
			if (twin) return label(twin.name, twin.code);
			return fallback;
		};

		const memberNames = (option: CatalogOption | undefined): string[] => {
			if (!option?.isPack) return [];
			return (catalog.packMembers.get(option.id) ?? [])
				.map((id) => catalog.byId.get(id))
				.filter((o): o is CatalogOption => !!o)
				.map((o) => o.name || o.subtitle || o.code)
				.filter(Boolean);
		};

		const lineItems: LineItem[] = lines
			.filter((l) => showTwins || !twinInfo.has(l.id))
			.map((l) => {
				const option = catalog.byId.get(l.id);
				return {
					id: l.id,
					name: nameOf(l.id, l.code),
					price: l.base,
					isPack: option?.isPack ?? false,
					members: memberNames(option),
				};
			})
			.sort((a, b) => {
				const ai = pickOrder.get(a.id) ?? Infinity;
				const bi = pickOrder.get(b.id) ?? Infinity;
				return ai !== bi ? ai - bi : a.id - b.id;
			});

		const listPriceVat = vehicle.prices.basePrice;

		return {
			listPriceVat,
			optionalsTotalVat: total.base,
			totalVat: listPriceVat + total.base,
			lineItems,
		};
	}, [data, engine, committedPicks, showTwins]);

	// in stock mode l'allestimento eventualmente selezionato è dormiente: i suoi
	// prezzi non devono mai comparire nel pannello
	return vehicleMode === 'stock'
		? stockPricing ?? EMPTY_PRICING
		: enginePricing;
}

const TAB_ORDER: CustomizerTab[] = ['colors', 'options', 'packs'];

const EMPTY_SPECS: VehicleSpecs = {
	equipment: [],
	performance: [],
	engine: [],
	dimensions: [],
	consumption: [],
	emissions: [],
};

function useVehicleSpecPanel() {
	const makeCode = useConfiguratorState((s) => s.makeCode);
	const modelCode = useConfiguratorState((s) => s.modelCode);
	const makeLabel = useConfiguratorState((s) => s.makeLabel);
	const modelLabel = useConfiguratorState((s) => s.modelLabel);
	const vehicleRef = useConfiguratorState((s) => s.vehicle);
	const subsection = useConfiguratorState((s) => s.subsection);
	const tab = useConfiguratorState((s) => s.tab);
	const setSubsection = useConfiguratorState((s) => s.setSubsection);
	const goToSummary = useConfiguratorState((s) => s.goToSummary);
	const clearSelections = useConfiguratorState((s) => s.clearSelections);
	const validating = useConfiguratorState((s) => s.validating);

	const vehicleMode = useConfiguratorState((s) => s.vehicleMode);
	const stockVehicle = useConfiguratorState((s) => s.stockVehicle);
	const confirmStockVehicle = useConfiguratorState(
		(s) => s.confirmStockVehicle
	);

	const domains = useConfiguratorState((s) => s.domains);
	const blockedBy = useConfiguratorState((s) => s.blockedBy);

	const trimsQuery = useTrims(makeCode, modelCode);
	const vehicleQuery = useVehicle(vehicleRef);
	const pricing = usePricing();
	const catalog = vehicleQuery.data?.catalog ?? null;

	// serie (and any other) accessories the current configuration excludes
	const excludedStandard = useMemo(() => {
		const set = new Set<OptionId>();
		for (const [id, domain] of domains) {
			if (domain === 'forbidden') set.add(id);
		}
		return set;
	}, [domains]);

	// human-readable names of the options that exclude each standard feature
	const standardBlockers = useMemo(() => {
		const map = new Map<OptionId, string[]>();
		if (!catalog) return map;
		for (const id of excludedStandard) {
			const names = (blockedBy.get(id) ?? [])
				.map((b) => catalog.byId.get(b))
				.filter((o): o is CatalogOption => !!o)
				.map((o) => (o.code ? `${o.name} [Cod. ${o.code}]` : o.name));
			map.set(id, names);
		}
		return map;
	}, [excludedStandard, blockedBy, catalog]);

	const trim =
		trimsQuery.data?.find(
			(t) => t.eurotaxCode && t.eurotaxCode === vehicleRef?.eurotaxCode
		) ?? null;
	const vehicle = vehicleQuery.data?.vehicle ?? null;

	const isLastStep =
		subsection !== 'engine' && tab === TAB_ORDER[TAB_ORDER.length - 1];

	const stockSpecs = useMemo(
		() => (stockVehicle ? buildStockSpecs(stockVehicle.specs) : null),
		[stockVehicle]
	);

	// in pronta consegna la scheda descrive l'auto già costruita: nome, allestimento,
	// foto e dati tecnici vengono dallo stock, mai dall'allestimento selezionato.
	if (vehicleMode === 'stock' && stockVehicle) {
		return {
			img: stockVehicle.photos[0] ?? null,
			photos: stockVehicle.photos,
			vehicleName: `${stockVehicle.make} ${stockVehicle.model}`.trim(),
			trim: stockVehicle.trim || null,
			fuel: null,
			fuelLabelOverride: titleCase(stockVehicle.specs.alimentazione) || null,
			listPriceVat: pricing.listPriceVat,
			optionalsTotalVat: pricing.optionalsTotalVat,
			totalVat: pricing.totalVat,
			lineItems: pricing.lineItems,
			specs: stockSpecs ?? EMPTY_SPECS,
			excludedStandard: new Set<OptionId>(),
			standardBlockers: new Map<OptionId, string[]>(),
			onContinue: () => confirmStockVehicle(),
			continueDisabled: false,
			// gli optional di un'auto in stock non sono rimovibili
			onClearAll: null,
			clearDisabled: true,
			isLastStep: true,
			isLoading: false,
			isError: false,
		};
	}

	return {
		img: vehicle?.heroPhoto || trim?.img || null,
		photos: vehicle?.photos ?? [],
		vehicleName:
			makeLabel && modelLabel ? `${makeLabel} ${modelLabel}` : null,
		trim: trim?.name ?? null,
		fuel: trim?.fuel ?? vehicle?.fuelType ?? null,
		fuelLabelOverride: null as string | null,
		listPriceVat: pricing.listPriceVat,
		optionalsTotalVat: pricing.optionalsTotalVat,
		totalVat: pricing.totalVat,
		lineItems: pricing.lineItems,
		specs: vehicle?.specs ?? EMPTY_SPECS,
		excludedStandard,
		standardBlockers,
		onContinue: () => {
			if (subsection === 'engine') setSubsection('exterior-interior');
			else void goToSummary();
		},
		continueDisabled: (subsection === 'engine' && !vehicleRef) || validating,
		onClearAll: (() => void clearSelections()) as (() => void) | null,
		clearDisabled: validating,
		isLastStep,
		isLoading: vehicleQuery.isLoading,
		isError: vehicleQuery.isError,
	};
}

function AccordionSection({
	title,
	defaultOpen = false,
	children,
}: {
	title: string;
	defaultOpen?: boolean;
	children: React.ReactNode;
}) {
	const [open, setOpen] = useState(defaultOpen);
	return (
		<div className={styles.accordionSection}>
			<button
				className={styles.accordionHeader}
				onClick={() => setOpen((o) => !o)}
			>
				<span>{title}</span>
				{open ? <BsChevronUp /> : <BsChevronDown />}
			</button>
			{open && <div className={styles.accordionBody}>{children}</div>}
		</div>
	);
}

function PriceLineItem({ item }: { item: LineItem }) {
	const [open, setOpen] = useState(false);

	if (item.isPack && item.members.length > 0) {
		return (
			<div>
				<button
					type="button"
					className={styles.packHeader}
					onClick={() => setOpen((o) => !o)}
				>
					<span className={styles.packHeaderLeft}>
						<span>{item.name}</span>
						{open ? <BsChevronUp /> : <BsChevronDown />}
					</span>
					<span className={styles.optPrice}>{formatCurrency(item.price)}</span>
				</button>
				{open && (
					<div className={styles.packChildren}>
						{item.members.map((m, i) => (
							<div key={`${item.id}-member-${i}`} className={styles.memberItem}>
								{m}
							</div>
						))}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className={styles.priceRow}>
			<span className={styles.optName}>{item.name}</span>
			<span className={styles.optPrice}>{formatCurrency(item.price)}</span>
		</div>
	);
}

type Section = {
	id: string;
	titleKey: string;
	titleDefault: string;
	rows: SpecRow[];
};

const toSections = (specs: VehicleSpecs): Section[] =>
	[
		{
			id: 'equipment',
			titleKey: 'car_configurator_customizer_standard_equipment',
			titleDefault: 'Equipaggiamenti di serie',
			rows: specs.equipment,
		},
		{
			id: 'engine',
			titleKey: 'car_configurator_customizer_engine_section',
			titleDefault: 'Motore',
			rows: specs.engine,
		},
		{
			id: 'dimensions',
			titleKey: 'car_configurator_customizer_dimensions_section',
			titleDefault: 'Dimensioni',
			rows: specs.dimensions,
		},
		{
			id: 'performance',
			titleKey: 'car_configurator_customizer_performance_section',
			titleDefault: 'Prestazioni',
			rows: specs.performance,
		},
		{
			id: 'emissions',
			titleKey: 'car_configurator_customizer_consumption_emissions',
			titleDefault: 'Consumi ed emissioni',
			rows: [...specs.consumption, ...specs.emissions],
		},
	].filter((s) => s.rows.length > 0);

function VehicleSpecSections({
	specs,
	fuelLabel,
	excludedStandard = new Set<OptionId>(),
	standardBlockers = new Map<OptionId, string[]>(),
}: {
	specs: VehicleSpecs;
	fuelLabel: string | null;
	excludedStandard?: Set<OptionId>;
	standardBlockers?: Map<OptionId, string[]>;
}) {
	const { t } = useTranslation();
	const [showAllEquip, setShowAllEquip] = useState(false);
	const equipPreview = useConfiguratorFeatures((f) => f.equipmentPreviewCount);

	const sections = toSections(specs);
	const hasEngineSection = sections.some((s) => s.id === 'engine');

	if (sections.length === 0 && !fuelLabel) return null;

	const renderLabel = (row: SpecRow) =>
		row.labelKey ? t(row.labelKey, { defaultValue: row.label }) : row.label;

	const fuelRow: SpecRow | null = fuelLabel
		? {
			id: 'fuel',
			label: t('car_configurator_customizer_fuel_type', {
				defaultValue: 'Alimentazione',
			}),
			value: fuelLabel,
		}
		: null;

	return (
		<>
			{sections.map((section, index) => {
				if (section.id === 'equipment') {
					const visible = showAllEquip
						? section.rows
						: section.rows.slice(0, equipPreview);
					return (
						<AccordionSection
							key={section.id}
							title={t(section.titleKey, {
								defaultValue: section.titleDefault,
							})}
							defaultOpen={index === 0}
						>
							{visible.map((row) => {
								const excluded =
									row.optionId != null && excludedStandard.has(row.optionId);
								const blockers =
									(row.optionId != null &&
										standardBlockers.get(row.optionId)) ||
									[];
								return (
									<div
										key={row.id}
										className={styles.equipItem}
										style={excluded ? { opacity: 0.55 } : undefined}
									>
										<BsCircle className={styles.equipIcon} />
										<span
											style={
												excluded
													? { textDecoration: 'line-through' }
													: undefined
											}
										>
											{row.label}
										</span>
										{excluded && (
											<span
												style={{
													fontSize: '0.72rem',
													fontStyle: 'italic',
													opacity: 0.85,
												}}
											>
												{blockers.length > 0
													? t('car_configurator_customizer_excluded_by', {
														defaultValue: '· escluso da {{options}}',
														options: blockers.join(', '),
													})
													: t('car_configurator_customizer_excluded_standard', {
														defaultValue: '· escluso dalla configurazione',
													})}
											</span>
										)}
									</div>
								);
							})}
							{section.rows.length > equipPreview && (
								<button
									className={styles.vediTutto}
									onClick={() => setShowAllEquip((prev) => !prev)}
								>
									{showAllEquip
										? t('car_configurator_customizer_see_less', {
											defaultValue: 'Vedi meno ›',
										})
										: t('car_configurator_customizer_see_all', {
											defaultValue: 'Vedi tutto ›',
										})}
								</button>
							)}
						</AccordionSection>
					);
				}

				const rows =
					section.id === 'engine' && fuelRow
						? [fuelRow, ...section.rows]
						: section.rows;

				return (
					<AccordionSection
						key={section.id}
						title={t(section.titleKey, { defaultValue: section.titleDefault })}
						defaultOpen={index === 0}
					>
						{rows.map((row) => (
							<div key={row.id} className={styles.specRow}>
								<span className={styles.specLabel}>{renderLabel(row)}</span>
								<span className={styles.specValue}>{row.value}</span>
							</div>
						))}
					</AccordionSection>
				);
			})}

			{!hasEngineSection && fuelRow && (
				<AccordionSection
					title={t('car_configurator_customizer_engine_section', {
						defaultValue: 'Motore',
					})}
				>
					<div className={styles.specRow}>
						<span className={styles.specLabel}>{fuelRow.label}</span>
						<span className={styles.specValue}>{fuelRow.value}</span>
					</div>
				</AccordionSection>
			)}
		</>
	);
}

function VehicleImage({
	photos,
	fallback,
	placeholder,
}: {
	photos: string[];
	fallback: string | null;
	placeholder: string;
}) {
	const sources = photos.length > 0 ? photos : fallback ? [fallback] : [];

	if (sources.length <= 1) {
		return (
			<img
				src={sources[0] ?? placeholder}
				alt=""
				className={styles.carImage}
				onError={(e) => {
					(e.currentTarget as HTMLImageElement).src = placeholder;
				}}
			/>
		);
	}

	return (
		<div className={styles.galleryWrap}>
			<ImageGallery
				items={sources.map((url) => ({ original: url, thumbnail: url }))}
				lazyLoad
				showPlayButton={false}
				showNav
				onErrorImageURL={placeholder}
			/>
		</div>
	);
}

function VehicleSpecPanelSkeleton() {
	return (
		<div className={styles.wrapper}>
			<div className={styles.imageCard}>
				<Skeleton className={styles.carImage} />
			</div>
			<div className={styles.infoCard}>
				<div className={styles.panelBody}>
					<Skeleton width="65%" height={20} />
					<Skeleton width="45%" height={14} className="mb-2" />
					<hr className={styles.divider} />
					<div className={styles.priceSection}>
						<Skeleton width="80%" height={16} />
						<Skeleton width="40%" height={12} />
						<Skeleton count={2} height={14} className="mt-2" />
					</div>
					<div className="mt-3">
						<Skeleton count={4} height={16} />
					</div>
				</div>
				<div className={styles.ctaSection}>
					<Skeleton height={48} />
				</div>
			</div>
		</div>
	);
}

type CtaBox = { left: number; width: number; height: number };

function useFixedCtaBox(
	anchor: HTMLElement | null,
	ctaEl: HTMLElement | null
): CtaBox | null {
	const [box, setBox] = useState<CtaBox | null>(null);

	useLayoutEffect(() => {
		const mq = window.matchMedia('(min-width: 992px)');

		const update = () => {
			if (!anchor || !mq.matches) {
				setBox(null);
				return;
			}
			const r = anchor.getBoundingClientRect();
			const height = ctaEl?.offsetHeight ?? 0;
			setBox((prev) =>
				prev &&
					prev.left === r.left &&
					prev.width === r.width &&
					prev.height === height
					? prev
					: { left: r.left, width: r.width, height }
			);
		};

		update();

		const ro = new ResizeObserver(update);
		if (anchor) ro.observe(anchor);
		if (ctaEl) ro.observe(ctaEl);
		window.addEventListener('resize', update);
		window.addEventListener('scroll', update, true);
		mq.addEventListener('change', update);

		return () => {
			ro.disconnect();
			window.removeEventListener('resize', update);
			window.removeEventListener('scroll', update, true);
			mq.removeEventListener('change', update);
		};
	}, [anchor, ctaEl]);

	return box;
}

export function VehicleSpecPanel() {
	const s = useVehicleSpecPanel();
	const { t } = useTranslation();
	const tr = useConfiguratorTranslations();
	const placeholder = 'https://placehold.co/400x225?text=Auto';

	const [infoCardEl, setInfoCardEl] = useState<HTMLDivElement | null>(null);
	const [ctaEl, setCtaEl] = useState<HTMLDivElement | null>(null);
	const ctaBox = useFixedCtaBox(infoCardEl, ctaEl);

	if (s.isLoading) return <VehicleSpecPanelSkeleton />;

	return (
		<div
			className={styles.wrapper}
			style={
				ctaBox
					? ({ '--cta-h': `${ctaBox.height}px` } as React.CSSProperties)
					: undefined
			}
		>
			<div className={styles.imageCard}>
				<VehicleImage
					photos={s.photos}
					fallback={s.img}
					placeholder={placeholder}
				/>
			</div>

			<div className={styles.infoCard} ref={setInfoCardEl}>
				<div className={styles.panelBody}>
					{s.vehicleName && (
						<p className={styles.vehicleName}>{s.vehicleName}</p>
					)}
					{s.trim && <p className={styles.trimName}>{s.trim}</p>}

					<hr className={styles.divider} />

					<div className={styles.priceSection}>
						<div className={styles.listPriceRow}>
							<span className={styles.listPriceLabel}>
								{t('car_configurator_customizer_list_price', {
									defaultValue: 'Prezzo di listino',
								})}
							</span>
							<span className={styles.listPriceValue}>
								{formatCurrency(s.listPriceVat)}
							</span>
						</div>
						<span className={styles.vatLabel}>
							{t('car_configurator_customizer_vat_excluded', {
								defaultValue: 'IVA Esclusa',
							})}
						</span>

						{s.lineItems.map((item) => (
							<PriceLineItem key={item.id} item={item} />
						))}

						{s.optionalsTotalVat > 0 && (
							<div className={`${styles.priceRow} ${styles.priceRowSpaced}`}>
								<span>
									{t('car_configurator_customizer_total_optionals', {
										defaultValue: 'Totale optional',
									})}
								</span>
								<span>+ {formatCurrency(s.optionalsTotalVat)}</span>
							</div>
						)}

						{s.lineItems.length > 0 && s.onClearAll && (
							<div className={styles.clearAllRow}>
								<button
									type="button"
									className={styles.clearAllBtn}
									disabled={s.clearDisabled}
									onClick={s.onClearAll}
								>
									{t('car_configurator_customizer_clear_all', {
										defaultValue: 'Rimuovi tutti',
									})}
								</button>
							</div>
						)}
					</div>

					<VehicleSpecSections
						specs={s.specs}
						fuelLabel={
							s.fuelLabelOverride ?? (s.fuel ? tr.fuelCodeLabel(s.fuel) : null)
						}
						excludedStandard={s.excludedStandard}
						standardBlockers={s.standardBlockers}
					/>
				</div>

				{!s.isError && (
					<div
						ref={setCtaEl}
						className={`${styles.ctaSection} ${ctaBox ? styles.ctaSectionFixed : ''
							}`}
						style={
							ctaBox ? { left: ctaBox.left, width: ctaBox.width } : undefined
						}
					>
						<div className={styles.ctaTotalRow}>
							<span>
								{t('car_configurator_customizer_total_configuration', {
									defaultValue: 'Totale configurazione',
								})}
							</span>
							<span className={styles.ctaTotalValue}>
								{formatCurrency(s.totalVat)}
							</span>
						</div>
						<span className={styles.vatLabel}>
							{t('car_configurator_customizer_vat_excluded', {
								defaultValue: 'IVA Esclusa',
							})}
						</span>
						<Button
							variant="primary"
							className="w-100"
							onClick={s.onContinue}
							disabled={s.continueDisabled}
						>
							{t('car_configurator_customizer_continue', {
								defaultValue: 'Continua',
							})}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

export function VehicleSpecPanelSummary() {
	const s = useVehicleSpecPanel();
	const { t } = useTranslation();
	const tr = useConfiguratorTranslations();
	const placeholder = `https://placehold.co/400x225?text=${s.vehicleName ?? 'Auto'
		}`;

	return (
		<div className={`${styles.wrapper} ${styles.wrapperStatic}`}>
			<div className={styles.imageCard}>
				<VehicleImage
					photos={s.photos}
					fallback={s.img}
					placeholder={placeholder}
				/>
			</div>

			<div className={styles.infoCard}>
				<div className={styles.panelBody}>
					{s.vehicleName && (
						<p className={styles.vehicleName}>{s.vehicleName}</p>
					)}
					{s.trim && <p className={styles.trimName}>{s.trim}</p>}

					{s.lineItems.length > 0 && (
						<>
							<hr className={styles.divider} />
							<div className={styles.summarySection}>
								<div className={styles.sectionLabel}>
									{t('car_configurator_customizer_selected_optionals', {
										defaultValue: 'Optional selezionati',
									})}
								</div>
								{s.lineItems.map((item) => (
									<div key={item.id} className={styles.summaryItem}>
										<span className={styles.checkMark}>✓</span>
										<span>{item.name}</span>
									</div>
								))}
							</div>
						</>
					)}

					<VehicleSpecSections
						specs={s.specs}
						fuelLabel={
							s.fuelLabelOverride ?? (s.fuel ? tr.fuelCodeLabel(s.fuel) : null)
						}
						excludedStandard={s.excludedStandard}
						standardBlockers={s.standardBlockers}
					/>
				</div>
			</div>
		</div>
	);
}
